// Utility to track user browsing and search history

import { getParsedUser } from './authStorage';

const getHistoryKey = (userId) => userId ? `raheasy_browsing_history_${userId}` : 'raheasy_browsing_history_guest';
const getSearchKey = (userId) => userId ? `raheasy_search_history_${userId}` : 'raheasy_search_history_guest';
const MAX_HISTORY_ITEMS = 50;

// Get current user ID
const getUserId = () => {
  const user = getParsedUser();
  return user ? user.id || user._id : null;
};

// Save product view to browsing history
export const trackProductView = (productId, productData = {}) => {
  try {
    const userId = getUserId();
    const historyKey = getHistoryKey(userId);
    const history = getBrowsingHistory();
    
    // Remove if already exists (to move to top)
    const filtered = history.filter(item => item.productId !== productId);
    
    // Add to beginning
    const newItem = {
      productId,
      category: productData.category,
      vendor: productData.vendor?.id || productData.vendor?.name,
      viewedAt: new Date().toISOString(),
      ...productData
    };
    
    const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(historyKey, JSON.stringify(updated));
  } catch (error) {
    console.error('Error tracking product view:', error);
  }
};

// Get browsing history
export const getBrowsingHistory = () => {
  try {
    const userId = getUserId();
    const historyKey = getHistoryKey(userId);
    const stored = localStorage.getItem(historyKey);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading browsing history:', error);
    return [];
  }
};

// Save search query to history
export const trackSearch = (query) => {
  if (!query || query.trim().length === 0) return;
  
  try {
    const userId = getUserId();
    const searchKey = getSearchKey(userId);
    const history = getSearchHistory();
    
    // Remove if already exists
    const filtered = history.filter(item => item.query.toLowerCase() !== query.toLowerCase());
    
    // Add to beginning
    const newItem = {
      query: query.trim(),
      searchedAt: new Date().toISOString()
    };
    
    const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(searchKey, JSON.stringify(updated));
  } catch (error) {
    console.error('Error tracking search:', error);
  }
};

// Get search history
export const getSearchHistory = () => {
  try {
    const userId = getUserId();
    const searchKey = getSearchKey(userId);
    const stored = localStorage.getItem(searchKey);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading search history:', error);
    return [];
  }
};

// Get recently viewed categories
export const getRecentCategories = (limit = 5) => {
  const history = getBrowsingHistory();
  const categories = history
    .map(item => item.category)
    .filter(Boolean)
    .filter((cat, index, self) => self.indexOf(cat) === index); // Unique
  return categories.slice(0, limit);
};

// Get recently viewed vendors
export const getRecentVendors = (limit = 5) => {
  const history = getBrowsingHistory();
  const vendors = history
    .map(item => item.vendor)
    .filter(Boolean)
    .filter((vendor, index, self) => self.indexOf(vendor) === index); // Unique
  return vendors.slice(0, limit);
};

// Clear browsing history
export const clearBrowsingHistory = () => {
  const userId = getUserId();
  const historyKey = getHistoryKey(userId);
  localStorage.removeItem(historyKey);
};

// Clear search history
export const clearSearchHistory = () => {
  const userId = getUserId();
  const searchKey = getSearchKey(userId);
  localStorage.removeItem(searchKey);
};

// Migrate guest history to user history (call on login)
export const migrateGuestHistoryToUser = (userId) => {
  try {
    const guestHistoryKey = getHistoryKey(null);
    const guestSearchKey = getSearchKey(null);
    const userHistoryKey = getHistoryKey(userId);
    const userSearchKey = getSearchKey(userId);
    
    // Migrate browsing history
    const guestHistory = localStorage.getItem(guestHistoryKey);
    if (guestHistory) {
      const userHistory = localStorage.getItem(userHistoryKey);
      const mergedHistory = userHistory 
        ? [...JSON.parse(guestHistory), ...JSON.parse(userHistory)]
            .filter((item, index, self) => 
              index === self.findIndex(t => t.productId === item.productId)
            )
            .slice(0, MAX_HISTORY_ITEMS)
        : JSON.parse(guestHistory);
      localStorage.setItem(userHistoryKey, JSON.stringify(mergedHistory));
      localStorage.removeItem(guestHistoryKey);
    }
    
    // Migrate search history
    const guestSearch = localStorage.getItem(guestSearchKey);
    if (guestSearch) {
      const userSearch = localStorage.getItem(userSearchKey);
      const mergedSearch = userSearch
        ? [...JSON.parse(guestSearch), ...JSON.parse(userSearch)]
            .filter((item, index, self) => 
              index === self.findIndex(t => t.query.toLowerCase() === item.query.toLowerCase())
            )
            .slice(0, MAX_HISTORY_ITEMS)
        : JSON.parse(guestSearch);
      localStorage.setItem(userSearchKey, JSON.stringify(mergedSearch));
      localStorage.removeItem(guestSearchKey);
    }
  } catch (error) {
    console.error('Error migrating guest history to user:', error);
  }
};
